import { useQuery } from 'react-query';
import { useMemo } from 'react';
import Text from '@raydiant/raydial/components/Text';
import Button from '@raydiant/raydial/components/Button';
import Card from '@raydiant/raydial/components/Card';
import CircleNodes from '@raydiant/raydial/components/Icons/CircleNodes';
import Dialog, { DialogTrigger } from '@raydiant/raydial/components/Dialog';
import Popover from '@raydiant/raydial/components/Popover';
import {
  CheckCircle,
  CircleExclamation,
} from '@raydiant/raydial/components/Icons';
import Page from '../../components/Page';
import PageLayout from '../../components/PageLayout/PageLayout';
import PageTitle from '../../components/PageLayout/PageTitle';
import history from '../../history';
import * as paths from '../../routes/paths';
import useConnections from '../../hooks/useConnections';
import LoadingModal from '@raydiant/raydial/components/LoadingModal';
import { IntegrationItem } from './components';
import config from '../../config';
import { t } from 'i18next';

const ProductHubIntegrationsPage = () => {
  const connectionsQuery = useConnections();

  const squareConnections = useMemo(
    () =>
      connectionsQuery.data?.data.filter(
        ({ connector }) => connector.name === 'square',
      ),
    [connectionsQuery?.data?.data],
  );

  const { data: squareIntegrationStatuses = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: () =>
      Promise.all(
        squareConnections?.map(async (connection) => {
          const { status } = await fetch(
            `${
              config.squareConnectorApiUrl
            }/product-catalog/locations?auth_id=${
              (connection.settings as { authId: string }).authId
            }`,
          );

          return [connection.id, status];
        }) || [],
      ),
    enabled: !!squareConnections?.length && squareConnections.length >= 1,
  });

  if (!connectionsQuery || connectionsQuery.isLoading) {
    return (
      <Page>
        <LoadingModal isOpen={true} />
      </Page>
    );
  }

  if (!connectionsQuery.data) {
    return null;
  }

  const { data: connections } = connectionsQuery.data;

  const handleOnAddIntegration = () => {
    history.push(paths.createIntegrations());
  };

  const handleOnEditClick = (integrationId: string) => {
    history.push(paths.editIntegrations(integrationId));
  };

  return (
    <Page>
      <PageLayout
        title={
          <PageTitle
            title="Integrations"
            subTitle="View and manage integrations"
          />
        }
      >
        <Card className="text-black max-w-[628px]">
          <header className="flex items-center px-4">
            <div className="grid gap-1 py-4 mr-auto">
              <Text as="h2" variant="subheader">
                Connected Integrations
              </Text>
              <Text as="h3" className="text-gray-400 font-regular text-lg">
                {connections.length === 0 &&
                  'You have no connected integrations'}
                {connections.length === 1 && 'You have 1 connected integration'}
                {connections.length > 1 &&
                  `You have ${connections.length} connected integrations`}
              </Text>
            </div>

            {connections.length > 0 && (
              <div>
                <Button onPress={handleOnAddIntegration}>
                  Add Integration
                </Button>
              </div>
            )}
          </header>
          {connections.length === 0 && (
            <IntegrationItem
              heading="Add an integration"
              subheading={`Connect with a ${t('companyName')} partner`}
              icon={
                <div className="size-16 bg-gray-200 rounded-lg flex items-center justify-center">
                  <CircleNodes className="text-gray-500 size-[30px]" />
                </div>
              }
              action={<Button onPress={handleOnAddIntegration}>Add</Button>}
            />
          )}
          {connections.length > 0 &&
            connections.map((integration) => {
              let squareIntegrationStatus: number | undefined;

              if (Array.isArray(squareIntegrationStatuses)) {
                const match = squareIntegrationStatuses.find(
                  (entry) =>
                    Array.isArray(entry) && entry[0] === integration.id,
                );

                if (typeof match?.[1] === 'number') {
                  squareIntegrationStatus = match[1];
                }
              }

              return (
                <IntegrationItem
                  key={integration.id}
                  heading={integration.name}
                  subheading={
                    <div className="flex">
                      {integration.connector.name}
                      {typeof squareIntegrationStatus === 'number' && (
                        <>
                          {' | '}
                          {squareIntegrationStatus === 200 && (
                            <span className="flex text-green-600">
                              <CheckCircle className="h-5" />
                              Online
                            </span>
                          )}

                          {squareIntegrationStatus === 401 && (
                            <DialogTrigger>
                              <Button aria-label="Help" variant="unstyled">
                                <span className="flex text-red-600">
                                  <CircleExclamation className="h-5" />
                                  Offline
                                </span>
                              </Button>
                              <Popover
                                className="max-w-[250px]"
                                placement="bottom start"
                              >
                                <Dialog>
                                  <div className="overflow-hidden text-black text-sm">
                                    <p className="font-semibold mb-2">
                                      Check your Square account
                                    </p>
                                    <p>
                                      Please make sure that {t('companyName')}{' '}
                                      is connected with your Square account in
                                      the Square dashboard.
                                    </p>
                                  </div>
                                </Dialog>
                              </Popover>
                            </DialogTrigger>
                          )}
                        </>
                      )}
                    </div>
                  }
                  icon={
                    <img
                      src={integration.connector.thumbnailUrl}
                      alt="connector"
                    />
                  }
                  action={
                    <Button
                      onPress={() => {
                        handleOnEditClick(integration.id);
                      }}
                    >
                      Edit
                    </Button>
                  }
                />
              );
            })}
        </Card>
      </PageLayout>
    </Page>
  );
};

export default ProductHubIntegrationsPage;
